"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _componentsBase = require("@telus-uds/components-base");
var _utils = require("../utils");
var _styles = require("./styles");
var _dictionary = _interopRequireDefault(require("./dictionary"));
var _SideButton = _interopRequireDefault(require("./SideButton"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const {
  isNaN
} = Number;
const isNumber = value => {
  return typeof value === 'number' && !isNaN(value);
};
const [selectProps, selectedSystemPropTypes] = (0, _componentsBase.selectSystemProps)([_componentsBase.a11yProps, _utils.htmlAttrs]);
const QuantitySelector = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    id = 'quantity-selector',
    minNumber,
    maxNumber,
    defaultValue,
    value,
    label,
    hint,
    hintPosition = 'inline',
    tooltip,
    onChange,
    dictionary = _dictionary.default,
    accessibilityLabel,
    copy = 'en',
    variant = {
      alternative: false
    },
    tokens,
    testID = 'quantity-selector-testid',
    ...rest
  } = _ref;
  const {
    disabled
  } = variant;
  const [error, setError] = _react.default.useState('');
  const getCopy = (0, _componentsBase.useCopy)({
    dictionary,
    copy
  });
  const getValidatedNumber = numberToEvaluate => {
    if (isNaN(numberToEvaluate)) return null;
    if (isNumber(minNumber) && numberToEvaluate < minNumber) throw getCopy('errors').numberIsTooSmall(minNumber);
    if (isNumber(maxNumber) && numberToEvaluate > maxNumber) throw getCopy('errors').numberIsTooBig(maxNumber);
    return numberToEvaluate;
  };
  const {
    currentValue: number,
    setValue: setNumber
  } = (0, _componentsBase.useInputValue)({
    value: getValidatedNumber(value),
    initialValue: getValidatedNumber(defaultValue),
    onChange
  });
  const isDecreaseEnabled = !disabled && !isNumber(minNumber) || number > minNumber;
  const isIncreaseEnabled = !disabled && !isNumber(maxNumber) || number < maxNumber;
  const inputValue = isNumber(number) ? number.toString() : '';
  const updateNumber = (newNumber, originalInputEvent) => {
    try {
      const validatedNumber = getValidatedNumber(newNumber);
      setNumber(validatedNumber, originalInputEvent);
      setError('');
    } catch (e) {
      setError(e);
    }
  };
  const inputChangeHandler = (textInputValue, event) => {
    if (textInputValue === '') setNumber(null);
    const numberFromTextInput = Number(textInputValue);
    if (isNumber(numberFromTextInput)) {
      updateNumber(numberFromTextInput, event);
    } else {
      setError(getCopy('errors').invalidCharacters);
    }
  };
  const renderLabel = () => label || hint ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.InputLabel, {
    forId: id,
    label: label,
    hint: hint,
    hintPosition: hintPosition,
    tooltip: tooltip
  }) : null;
  const getTokens = (0, _componentsBase.useThemeTokensCallback)('QuantitySelector', tokens, variant);
  const renderTextInput = () => /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.TextInput, {
    nativeID: id,
    value: inputValue,
    defaultValue: defaultValue,
    tokens: textInputState => {
      const {
        inputWidth,
        inputBorderWidth,
        inputBorderColor,
        textColor,
        inputBackgroundColor,
        ...restOfTokens
      } = getTokens({
        ...textInputState
      });
      return {
        ...restOfTokens,
        order: 1,
        borderWidth: inputBorderWidth,
        backgroundColor: inputBackgroundColor,
        color: textColor,
        width: inputWidth,
        borderColor: inputBorderColor,
        borderRadius: 0,
        outerBorderWidth: 0
      };
    },
    onChange: inputChangeHandler
    // Using title as an accessibility workaround
    // given that accessibilityLabel is not available
    ,
    keyboardType: "numeric",
    accessibilityLabel: accessibilityLabel ?? getCopy('accessibility').a11yLabel,
    accessibilityRole: "spinbutton",
    accessibilityValueMax: maxNumber,
    accessibilityValueMin: minNumber,
    accessibilityValueNow: number
  });
  const flexOrder = order => ({
    order
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_componentsBase.Box, {
    testID: testID,
    ref: ref,
    ...selectProps(rest),
    children: [renderLabel(), /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Spacer, {
      space: 2
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_styles.InputWrapper, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_styles.InputField, {
        children: renderTextInput()
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: flexOrder(0),
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_SideButton.default, {
          isEnabled: isDecreaseEnabled,
          onPress: () => updateNumber(number - 1),
          onDoubleClick: () => updateNumber(number - 1),
          tokens: tokens,
          variant: {
            decrease: true,
            ...variant
          },
          accessibilityLabel: getCopy('accessibility').decreaseButton,
          accessibilityDisabled: !isDecreaseEnabled
        })
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: flexOrder(2),
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_SideButton.default, {
          isEnabled: isIncreaseEnabled,
          onPress: () => updateNumber(number + 1),
          onDoubleClick: () => updateNumber(number + 1),
          accessibilityLabel: getCopy('accessibility').increaseButton,
          accessibilityDisabled: !isIncreaseEnabled,
          tokens: tokens,
          variant: {
            increase: true,
            ...variant
          }
        })
      })]
    }), error ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Box, {
      vertical: 2,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Feedback, {
        validation: "error",
        children: error
      })
    }) : null]
  });
});

// If a language dictionary entry is provided, it must contain every key
const dictionaryContentShape = _propTypes.default.shape({
  accessibility: _propTypes.default.shape({
    a11yLabel: _propTypes.default.string.isRequired,
    increaseButton: _propTypes.default.string.isRequired,
    decreaseButton: _propTypes.default.string.isRequired
  }).isRequired,
  errors: _propTypes.default.shape({
    numberIsTooSmall: _propTypes.default.func.isRequired,
    numberIsTooBig: _propTypes.default.func.isRequired,
    invalidCharacters: _propTypes.default.string.isRequired
  }).isRequired
});
QuantitySelector.displayName = 'QuantitySelector';
QuantitySelector.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * The id of the input field
   */
  id: _propTypes.default.string,
  /**
   * The minimum number allowed
   */
  minNumber: _propTypes.default.number,
  /**
   * The maximum number allowed
   */
  maxNumber: _propTypes.default.number,
  /**
   * The callback function that is called when the value of the input field changes
   */
  onChange: _propTypes.default.func,
  /**
   * The default value of the input field
   */
  defaultValue: _propTypes.default.number,
  /**
   * If the input's state is to be controlled by a parent component, use this prop
   * together with the `onChange` to pass down and update the lifted state.
   */
  value: _propTypes.default.number,
  /**
   * The label of the input field
   */
  label: _propTypes.default.string,
  /**
   * The hint of the input field
   */
  hint: _propTypes.default.string,
  /**
   * The position of the hint. Could be shown along side the label or below it
   */
  hintPosition: _propTypes.default.oneOf(['inline', 'below']),
  /**
   * Adds a question mark which will display a tooltip when clicked
   */
  tooltip: _propTypes.default.string,
  /**
   * The accessibility label of the input field
   */
  accessibilityLabel: _propTypes.default.string,
  /**
   * The dictionary object containing the content for the input field
   */
  dictionary: _propTypes.default.shape({
    en: dictionaryContentShape,
    fr: dictionaryContentShape
  }),
  /**
   * The language to use for the copy.
   */
  copy: _propTypes.default.oneOfType([_propTypes.default.oneOf(['en', 'fr'])]),
  variant: _propTypes.default.exact({
    alternative: _propTypes.default.bool,
    disabled: _propTypes.default.bool
  }),
  tokens: (0, _componentsBase.getTokensPropType)('QuantitySelector'),
  /**
   * Sets `data-testid` attribute on the input field for testing purposes.
   */
  testID: _propTypes.default.string
};
var _default = exports.default = QuantitySelector;